import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaQuoteRight, FaStar } from 'react-icons/fa';
import { testimonialService } from '../../services/testimonialService';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTestimonials = async () => {
    try {
      const data = await testimonialService.getPublicTestimonials();
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Could not fetch testimonials:', err);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    window.addEventListener('testimonials-updated', fetchTestimonials);
    return () => {
      window.removeEventListener('testimonials-updated', fetchTestimonials);
    };
  }, []);

  // إذا لم تكن هناك أي آراء معتمدة في قاعدة البيانات، لا نعرض القسم ولا نعرض أي بيانات وهمية
  if (!loading && (!testimonials || testimonials.length === 0)) {
    return null;
  }

  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Decorative Blob */}
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl font-extrabold text-[#020B14] mb-4">
            آراء <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">المتدربين</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-blue-600/30" />
            <div className="w-2.5 h-2.5 rounded-full bg-blue-600 rotate-45" />
            <div className="w-12 h-0.5 bg-gradient-to-l from-transparent to-blue-600/30" />
          </div>
          <p className="text-lg max-w-2xl mx-auto font-normal leading-relaxed" style={{ color: '#282828' }}>
            نسعد بمشاركة قصص نجاح خريجينا الذين طوروا قدراتهم وحققوا قفزات مهنية حقيقية مع DATX.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            const starsCount = Math.max(1, Math.min(5, Number(testimonial.rating) || 5));

            return (
              <motion.div
                key={testimonial.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-100 hover:border-blue-500/10 shadow-md hover:shadow-xl transition-all duration-300 group hover:-translate-y-1.5 flex flex-col relative"
              >
                {/* Quote icon at the top corner */}
                <div className="absolute top-6 left-6 text-slate-100 group-hover:text-blue-50 transition-colors duration-300">
                  <FaQuoteRight className="w-10 h-10 transform rotate-180" />
                </div>

                {/* User profile info */}
                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <img
                    src={testimonial.image || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=120&h=120&q=80'}
                    alt={testimonial.name}
                    width="56"
                    height="56"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=120&h=120&q=80';
                    }}
                    className="w-14 h-14 rounded-full border-2 border-blue-100 object-cover flex-shrink-0"
                  />
                  <div>
                    <h3 className="font-bold text-[#020B14] group-hover:text-blue-600 transition-colors">
                      {testimonial.name}
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      {testimonial.role} في <span className="text-blue-600 font-semibold">{testimonial.company}</span>
                    </p>
                  </div>
                </div>

                {/* Testimonial body content */}
                <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow relative z-10 font-normal">
                  "{testimonial.content}"
                </p>

                {/* Star review system */}
                <div className="flex items-center gap-1 border-t border-slate-50 pt-4 mt-auto">
                  {[...Array(starsCount)].map((_, i) => (
                    <FaStar key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;